import type { Unsubscribe } from '@reduxjs/toolkit';
import h from 'hyperscript';

import { BackendAbortController } from '@disy/lib.backend/backend-abort-controller';
import { assert } from '@disy/lib/custom-error';
import type { EventKey } from '@disy/lib/event-util';
import { Observable, on, unByKey } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';

import { Command } from 'cadenza/api-client/workbook-commands';
import { getTimelinesFor2d } from 'cadenza/api-client/workbook-timeline-api';
import { loadTimelines } from 'cadenza/map/timeline-visualization/load-timelines';
import type { TimelinePlayer } from 'cadenza/map/timeline-visualization/timeline-player';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore, unsubscribeStore } from 'cadenza/store/state-utils';
import type { CREATE_DATE_FILTER_EVENT_PAYLOAD } from 'cadenza/utils/timeline-utils';
import { CREATE_DATE_FILTER_EVENT } from 'cadenza/utils/timeline-utils';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getMapViewSrs } from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import { viewHasTimelineData } from 'cadenza/workbook/state/workbook-timeline';
import {
  closeTimelinePlayer,
  getTimelinePlayerMapViewId,
  getTimelines,
  isTimelinePlayerOpen,
  isUpdatingTimelines,
} from 'cadenza/workbook/timelines/timeline.state';
import type { Workbook } from 'cadenza/workbook/workbook';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getDataViewById,
  getInvalidViewsOrDataViews,
  getSelection,
  getWorkbookNameAndHash,
} from 'cadenza/workbook/workbook-core-selectors.state';

export class TimelinesCoordinator extends Observable {
  readonly _workbook: Workbook;

  _unsubscribeFromStore: Unsubscribe[] = [];
  _timelinePlayer: TimelinePlayer | null = null;
  _timelinePlayerFilterEventListener: EventKey[] = [];
  _abortController?: BackendAbortController; // Present while requests are running, only

  constructor(workbook: Workbook) {
    super();
    this._workbook = workbook;
  }

  initialize() {
    this._unsubscribeFromStore = [
      observeStore(getStore(), getInvalidViewsOrDataViews, () => this._updateState(), true),
      observeStore(getStore(), getSelection, (selection) => this._timelinePlayer?.updateSelection(selection), true),
      observeStore(
        getStore(),
        isTimelinePlayerOpen,
        (open, wasOpen) => {
          if (open) {
            const viewId = getTimelinePlayerMapViewId(getStore().getState())!; // View is available when open
            this._showTimelinePlayer(viewId);
          } else if (wasOpen) {
            this._removeTimelinePlayer();
          }
        },
        true,
      ),

      observeStore(getStore(), isUpdatingTimelines, (updating) => {
        if (updating) {
          const viewId = getTimelinePlayerMapViewId(getStore().getState())!; // In updating situation the viewId is defined
          const idAndHash = getWorkbookNameAndHash(getStore().getState());
          const srsCode = getMapViewSrs(getStore().getState(), viewId)!;

          loadTimelines((allowLargeData) => {
            this._abortController = new BackendAbortController();

            return getTimelinesFor2d(idAndHash, viewId, srsCode, allowLargeData, this._abortController.signal).finally(
              () => {
                this._abortController = undefined;
              },
            );
          });
        } else {
          this._abortController?.abort();
        }
      }),
    ];
  }

  deinitialize() {
    this._unsubscribeFromStore = unsubscribeStore(this._unsubscribeFromStore);
  }

  _updateState() {
    const timelinePlayerMapViewId = getTimelinePlayerMapViewId(getStore().getState());
    if (timelinePlayerMapViewId) {
      const hasTimelineData = viewHasTimelineData(getStore().getState(), timelinePlayerMapViewId);
      if (!hasTimelineData) {
        this._stopTimelinePlayerAndResetState();
      }
    }
  }

  /**
   * Creates a new instance of {@link TimelinePlayer} and adds it to the DOM.
   *
   * @param viewId - The ID of the View that
   *   requests to show the TimelinePLayer.
   */
  _showTimelinePlayer(viewId: ViewId) {
    assert(viewId, 'Parameter viewId is mandatory.');

    if (this._workbook.worksheet) {
      // import timeline-player dynamically to avoid OpenLayers lib in the workbook parent page
      import('cadenza/map/timeline-visualization/timeline-player')
        .then(({ TimelinePlayer }) => {
          const timelinePlayer = new TimelinePlayer({
            allowSettingStartEndFilters: true,
            pointOverlaysEnabled: true,
            selection: getSelection(getStore().getState()),
          });
          this._workbook.worksheet.insertBeforeOnDemandOverlay(
            h('.d-timeline-player--container-workbook', timelinePlayer),
          );
          this._timelinePlayer = timelinePlayer;
          this._timelinePlayerFilterEventListener.push(
            on(this._timelinePlayer, CREATE_DATE_FILTER_EVENT, (event: CustomEvent<CREATE_DATE_FILTER_EVENT_PAYLOAD>) =>
              setFiltersForDataViews(event.detail),
            ),
          );
        })
        .catch((error) => logger.error('Failed to show timeline player.', error));
    }
  }

  _removeTimelinePlayer() {
    if (this._timelinePlayer) {
      this._stopTimelinePlayerAndResetState();
      this._workbook.querySelector<HTMLElement>('.d-timeline-player--container-workbook')!.remove();
      this._timelinePlayer = null;
      unByKey(this._timelinePlayerFilterEventListener);
    }
  }

  _stopTimelinePlayerAndResetState() {
    // Because this update could be part of a subscribeStore callback we must defer it to the next
    // event loop. It would be better to integrate the flag update into the reducer of time set action.
    queueMicrotask(() => getStore().dispatch(closeTimelinePlayer()));
  }
}

function setFiltersForDataViews(eventDetails: CREATE_DATE_FILTER_EVENT_PAYLOAD) {
  // this action is initiated by timeline player so we know that timelines are there
  const timelinesInPlayer = getTimelines(getStore().getState())!;
  const commands = timelinesInPlayer.timelines.map((t) =>
    createSetDateFilterCommand(t.layerId as DataViewId, eventDetails),
  );

  getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
}

function createSetDateFilterCommand(
  dataViewId: DataViewId,
  { start, end, overrideMode }: CREATE_DATE_FILTER_EVENT_PAYLOAD,
) {
  // since this data view is played by timeline player, we know for sure it has "SEQUENCE_ORDER"
  // bundle with only 1 date attribute in it.
  const dateAttributeId = getDataViewById(getStore().getState(), dataViewId).attributes['SEQUENCE_ORDER']![0];
  return Command.CREATE_DATE_FILTER({
    dataViewId,
    dateAttributeId,
    start,
    end,
    overrideMode,
  });
}
